import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { ListFilter, X } from 'lucide-react'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import { cn } from '@/lib/utils'
import { InboxAssistantOptions } from './InboxAssistantOptions'
import { InboxDateRangeFilter } from './InboxDateRangeFilter'
import type { InboxFilterOption } from './InboxFilterOptions'
import { EMPTY_INBOX_FILTERS, type InboxFilters } from './inboxFilters'
import type { InboxFilterSection } from './inboxFilterSection'

/** Option lists longer than this get a search field. Short lists stay a plain checkbox group. */
const SEARCH_WHEN_MORE_THAN = 8
/** Choices shown before the rest of a mobile filter. Keeps the next filter within reach. */
const PREVIEW_LIMIT = 4

/**
 * Phones open the keyboard whenever a text field is focused, including the focus
 * the browser applies while this sheet is opening. The field stays read-only until
 * the user taps it.
 */
function useTapToType(mode: 'text' | 'tel' = 'text') {
  const [editable, setEditable] = useState(false)
  return {
    readOnly: !editable,
    inputMode: (editable ? mode : 'none') as 'text' | 'tel' | 'none',
    onPointerDown: (event: React.PointerEvent<HTMLInputElement>) => {
      event.currentTarget.readOnly = false
      event.currentTarget.inputMode = mode
      setEditable(true)
    },
  }
}

interface InboxMobileFiltersProps {
  filters: InboxFilters
  onApply: (filters: InboxFilters) => void
  sectionsFor: (filters: InboxFilters, update: (next: InboxFilters) => void) => InboxFilterSection[]
}

export function InboxMobileFilters({ filters, onApply, sectionsFor }: InboxMobileFiltersProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const dialogRef = useRef<HTMLElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState(filters)
  const activeFilters = sectionsFor(filters, () => undefined).filter(
    (section) => section.activeCount > 0
  ).length
  const sections = isOpen ? sectionsFor(draft, setDraft) : []

  const open = () => {
    setDraft(filters)
    setIsOpen(true)
  }

  useEffect(() => {
    const root = dialogRef.current
    if (!isOpen || !root) return

    const blocked = (target: EventTarget | null): target is HTMLInputElement =>
      target instanceof HTMLInputElement &&
      target.readOnly &&
      (target.type === 'text' || target.type === 'search' || target.type === 'tel')

    const onFocusIn = (event: FocusEvent) => {
      if (!blocked(event.target)) return
      root.focus({ preventScroll: true })
    }

    root.addEventListener('focusin', onFocusIn)
    if (blocked(document.activeElement)) root.focus({ preventScroll: true })
    return () => root.removeEventListener('focusin', onFocusIn)
  }, [isOpen])

  const apply = () => {
    onApply({
      ...draft,
      topic: draft.topic.trim(),
      summary: draft.summary.trim(),
      phone_number: draft.phone_number.trim(),
    })
    setIsOpen(false)
  }

  return (
    <>
      <button
        type="button"
        onClick={open}
        aria-label={t('resources.conversations.inbox.filters.menu')}
        className={cn(
          'outline-focus-ring relative flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 focus-visible:outline-2 dark:text-gray-400 dark:hover:bg-gray-800',
          activeFilters > 0 && 'text-gray-900 dark:text-gray-100'
        )}
      >
        <ListFilter className="size-5" strokeWidth={2.5} aria-hidden="true" />
        {activeFilters > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-gray-700 text-[10px] leading-none font-medium text-white">
            {activeFilters}
          </span>
        )}
      </button>

      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        isDismissable
        className={({ isEntering, isExiting }) =>
          cn(
            'fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-2',
            isEntering && 'animate-in fade-in duration-300 ease-out',
            isExiting && 'animate-out fade-out duration-200 ease-in'
          )
        }
      >
        <Modal
          className={({ isEntering, isExiting }) =>
            cn(
              'flex h-[calc(100dvh-1rem)] w-full max-w-lg flex-col',
              isEntering && 'animate-in slide-in-from-bottom duration-300 ease-out',
              isExiting && 'animate-out slide-out-to-bottom duration-200 ease-in'
            )
          }
        >
          <Dialog
            ref={dialogRef}
            aria-labelledby={titleId}
            className="flex h-full min-h-0 flex-col overflow-hidden rounded-[28px] bg-white outline-hidden dark:bg-gray-950"
          >
            <div className="flex items-center justify-between px-4 pt-4">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label={t('resources.generic.close')}
                className="outline-focus-ring flex size-9 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 dark:bg-gray-800 dark:text-gray-100"
              >
                <X className="size-5" strokeWidth={2.25} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setDraft(EMPTY_INBOX_FILTERS)}
                className="outline-focus-ring h-9 cursor-pointer rounded-full bg-gray-100 px-3.5 text-sm font-medium text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 dark:bg-gray-800 dark:text-gray-100"
              >
                {t('resources.generic.reset')}
              </button>
            </div>

            <h2
              id={titleId}
              className="px-5 pt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white"
            >
              {t('resources.conversations.inbox.filters.menu')}
            </h2>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-5 pb-2">
              <div className="flex flex-col gap-4">
                {sections.map((section) => (
                  <FilterBlock key={section.id} section={section} />
                ))}
              </div>
            </div>

            <div className="shrink-0 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <button
                type="button"
                onClick={apply}
                className="outline-focus-ring h-12 w-full cursor-pointer rounded-full bg-gray-900 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 dark:bg-white dark:text-gray-900"
              >
                {t('resources.generic.apply_filters')}
              </button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  )
}

function FilterBlock({ section }: { section: InboxFilterSection }) {
  const labelId = `${section.id}-label`
  const tapToType = useTapToType(section.kind === 'text' ? (section.inputMode ?? 'text') : 'text')

  return (
    <section className="flex flex-col gap-2" aria-labelledby={labelId}>
      <h3 id={labelId} className="px-1 text-sm font-medium text-gray-500 dark:text-gray-400">
        {section.label}
      </h3>
      {section.kind === 'assistants' ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <InboxAssistantOptions
            selected={section.selected}
            onChange={section.onChange}
            autoFocus={false}
            scrollable={false}
            previewLimit={PREVIEW_LIMIT}
          />
        </div>
      ) : section.kind === 'options' ? (
        <MobileOptions
          labelId={labelId}
          options={section.options}
          selected={section.selected}
          onChange={section.onChange}
          selection={section.selection}
          allowsCustomValue={section.allowsCustomValue}
        />
      ) : section.kind === 'text' ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4">
          <input
            type="text"
            value={section.value}
            onChange={(event) => section.onChange(event.target.value)}
            placeholder={section.placeholder}
            aria-label={section.placeholder}
            className="h-10 w-full bg-transparent text-base text-gray-900 outline-hidden placeholder:text-gray-400"
            {...tapToType}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <InboxDateRangeFilter from={section.from} to={section.to} onChange={section.onChange} />
        </div>
      )}
    </section>
  )
}

function MobileOptions({
  labelId,
  options,
  selected,
  onChange,
  selection = 'multiple',
  allowsCustomValue = false,
}: {
  labelId: string
  options: InboxFilterOption<string | number>[]
  selected: Array<string | number>
  onChange: (selected: Array<string | number>) => void
  selection?: 'single' | 'multiple'
  allowsCustomValue?: boolean
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(false)
  const tapToType = useTapToType()

  const allOptions = useMemo(() => {
    if (!allowsCustomValue) return options
    const custom = selected
      .filter((value) => !options.some((option) => option.value === value))
      .map((value) => ({ value, label: String(value) }))
    return [...options, ...custom]
  }, [allowsCustomValue, options, selected])

  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? allOptions.filter((option) => option.label.toLowerCase().includes(q)) : allOptions
  }, [allOptions, query])

  const showSearch = allowsCustomValue || allOptions.length > SEARCH_WHEN_MORE_THAN
  const customValue = query.trim()
  const showCustomValue =
    allowsCustomValue &&
    customValue !== '' &&
    !allOptions.some((option) => option.value === customValue || option.label === customValue)

  const shownOptions = useMemo(() => {
    if (expanded || query.trim() !== '') return visibleOptions
    const picked = visibleOptions.filter((option) => selected.includes(option.value))
    const rest = visibleOptions.filter((option) => !selected.includes(option.value))
    return [...picked, ...rest.slice(0, Math.max(PREVIEW_LIMIT - picked.length, 0))]
  }, [expanded, query, selected, visibleOptions])
  const hiddenCount = visibleOptions.length - shownOptions.length
  const canTogglePreview = query.trim() === '' && visibleOptions.length > PREVIEW_LIMIT

  const toggle = (value: string | number, isSelected: boolean) => {
    if (selection === 'single') {
      onChange(isSelected ? [value] : [])
      return
    }
    onChange(isSelected ? [...selected, value] : selected.filter((item) => item !== value))
  }

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
    >
      {showSearch && (
        <div className="border-b border-gray-200 px-4 py-1">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('resources.conversations.inbox.filter_search')}
            aria-label={t('resources.conversations.inbox.filter_search')}
            className="h-10 w-full bg-transparent text-base text-gray-900 outline-hidden placeholder:text-gray-400"
            {...tapToType}
          />
        </div>
      )}

      {showCustomValue && (
        <button
          type="button"
          className="w-full cursor-pointer px-4 py-3 text-left text-base text-gray-900"
          onClick={() => {
            onChange(selection === 'single' ? [customValue] : [...selected, customValue])
            setQuery('')
          }}
        >
          {t('resources.conversations.inbox.filter_custom', { value: customValue })}
        </button>
      )}

      {visibleOptions.length > 0 ? (
        <div>
          {shownOptions.map((option) => (
            <Checkbox
              key={String(option.value)}
              size="sm"
              label={<span className="font-normal text-gray-900">{option.label}</span>}
              isSelected={selected.includes(option.value)}
              onChange={(isSelected) => toggle(option.value, isSelected)}
              className="w-full cursor-pointer items-center px-4 py-2"
            />
          ))}
          {canTogglePreview && (
            <button
              type="button"
              className="w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-500"
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded
                ? t('resources.conversations.inbox.show_less_options')
                : t('resources.conversations.inbox.show_more_options', { count: hiddenCount })}
            </button>
          )}
        </div>
      ) : (
        !showCustomValue && (
          <p className="px-4 py-6 text-center text-sm text-gray-500">
            {t('resources.conversations.inbox.no_results')}
          </p>
        )
      )}
    </div>
  )
}
